/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  initPageable,
  initSlicePageable,
  PaginationResponse,
  SlicePaginationResponse,
  IErrorMessage,
  defaultPatientDetail,
  defaultStudyDetail,
  defaultSeriesDetail,
  IStudyState,
  PaginateState,
  IStudyDetail,
  IBreakGlassPatientResponse,
} from "@advapacs/common";
import { mfaSensitiveApi } from "../../../config/api";
import { createHttpRequestAsyncThunk } from "./operators";
import { axiosInstanceRegion } from "../../../config/axios-region";

export const initPaginate: PaginateState = {
  numberOfElements: 0,
  offset: 0,
  pageNumber: 0,
  last: true,
  first: true,
  totalElements: 0,
};

const initPaginationResponse: PaginationResponse<any> = {
  content: [],
  pageable: initPageable,
};

const initialState: IStudyState = {
  loading: false,
  loadingSeriesDetail: false,
  loadingStudyDetail: false,
  needReloadStudies: false,
  finishedLoadSeries: false,
  finishedLoadInstances: false,
  errorMessage: null,
  studies: [],
  studiesAccessGrants: [],
  accessGrantLoading: false,
  moveStudyModel: {
    loading: false,
    isMoved: false,
    errorMessage: null,
  },
  isCascading: false,
  patientDetail: defaultPatientDetail,
  studyDetail: defaultStudyDetail,
  seriesDetail: defaultSeriesDetail,
  allSeries: [],
  series: [],
  instances: [],
  pageableSeries: initPageable,
  pageableStudy: initSlicePageable,
  pageableInstance: initPageable,
  instanceHeader: [],
  deletedMessage: "",
  documents: initPaginationResponse,
  loadingDocuments: false,
  viewSharesListStudy: initPaginationResponse,
  loadingViewSharesListStudy: false,
  isOnlySearchingSeries: false,
  zedLoading: false,
  accessDeniedErrorMessage: undefined,
  pageableStudyAccessGrant: initSlicePageable,
  downloading: false,
  breakGlassErrorMessage: null,
  phoneVerification: {
    readyVerifyCode: false,
    loading: false,
    errorMessage: null,
    verifyCodeSuccessful: false,
  },
};

export const sendPhoneVerificationRequest = createHttpRequestAsyncThunk(
  "study/sendPhoneVerificationRequest",
  mfaSensitiveApi.patientVerifyRequest,
  {
    method: "POST",
    getAxiosInstance: () => axiosInstanceRegion,
  }
);

export const sendPhoneVerificationCode = createHttpRequestAsyncThunk(
  "study/sendPhoneVerificationCode",
  mfaSensitiveApi.patientVerifyCode,
  {
    method: "POST",
    getAxiosInstance: () => axiosInstanceRegion,
  }
);

const studySlice = createSlice({
  name: "study",
  initialState,
  reducers: {
    getStudiesStart(state) {
      state.loading = true;
      state.studies = [];
    },
    getStudiesSuccess(
      state,
      action: PayloadAction<SlicePaginationResponse<IStudyDetail>>
    ) {
      const { content, pageable } = action.payload;
      state.loading = false;
      state.isCascading = false;
      state.studies = content;
      state.pageableStudy = pageable;
      state.finishedLoadInstances = false;
      state.finishedLoadSeries = false;
    },
    getStudiesFail(state, action: PayloadAction<IErrorMessage>) {
      state.loading = false;
      if (action.payload?.code === "403") {
        state.accessDeniedErrorMessage = "access_denied_study";
      } else {
        state.errorMessage = action.payload;
      }
      state.finishedLoadInstances = false;
      state.finishedLoadSeries = false;
    },
    getBreakGlassPatientsStart(state) {
      state.breakGlassErrorMessage = null;
      state.accessGrantLoading = true;
    },
    getBreakGlassPatientsSuccess(
      state,
      action: PayloadAction<SlicePaginationResponse<IBreakGlassPatientResponse>>
    ) {
      const { content, pageable } = action.payload;
      state.accessGrantLoading = false;
      state.studiesAccessGrants = content;
      state.pageableStudyAccessGrant = pageable;
    },
    getBreakGlassPatientsFail(state, action: PayloadAction<IErrorMessage>) {
      state.accessGrantLoading = false;
      state.breakGlassErrorMessage = action.payload;
    },
    setShowPatientVerifySmsModal(
      state,
      action: PayloadAction<{ show: boolean; studyIds: string[] }>
    ) {
      state.showPatientVerifySmsModal = action.payload?.show;
      state.phoneVerification = {
        ...state.phoneVerification,
        studyIds: action.payload?.studyIds,
      };
    },
    setErrorMessage(state, action) {
      state.errorMessage = action.payload;
    },
    clearBreakGlassErr(state) {
      state.breakGlassErrorMessage = null;
    },
    clearAccessDeniedErrorMessage(state) {
      state.accessDeniedErrorMessage = undefined;
    },
    clearState(state) {
      state.errorMessage = null;
      state.breakGlassErrorMessage = null;
      state.accessDeniedErrorMessage = undefined;
      state.pageableStudy = initSlicePageable;
      state.studies = [];
    },
    getReportStart(state) {
      state.loading = true;
    },
    getReportSuccess(state, action) {
      state.loading = false;
      state.reportDetail = action.payload;
    },
    getReportFail(state) {
      state.loading = false;
    },
    getReportContentSuccess(state) {
      state.loading = false;
    },
    clearAccessGrantData(state) {
      state.pageableStudyAccessGrant = initSlicePageable;
      state.studiesAccessGrants = [];
    },
    downloadStart(state) {
      state.downloading = true;
    },
    downloadSuccess(state) {
      state.downloading = false;
    },
    downloadFail(state, action: PayloadAction<IErrorMessage>) {
      state.downloading = false;
      state.errorMessage = action.payload;
    },
    updateStudyVerifying(state, action: PayloadAction<IStudyDetail>) {
      state.phoneVerification = {
        ...state.phoneVerification,
        verifyingStudy: action.payload,
      };
    },
    resetPhoneVerification(state) {
      state.phoneVerification = {
        readyVerifyCode: false,
        loading: false,
        errorMessage: null,
        verifyCodeSuccessful: false,
        studyIds: [],
      };
    },
  },
  extraReducers(builder) {
    builder.addCase(sendPhoneVerificationRequest.pending, (state) => {
      state.phoneVerification = {
        ...state.phoneVerification,
        loading: true,
        verifyCodeSuccessful: false,
        readyVerifyCode: false,
        errorMessage: null,
      };
    });
    builder.addCase(sendPhoneVerificationRequest.fulfilled, (state) => {
      state.phoneVerification = {
        ...state.phoneVerification,
        loading: false,
        readyVerifyCode: true,
      };
    });
    builder.addCase(sendPhoneVerificationRequest.rejected, (state, action) => {
      state.phoneVerification = {
        ...state.phoneVerification,
        loading: false,
        errorMessage: action.payload as IErrorMessage,
      };
    });
    builder.addCase(sendPhoneVerificationCode.pending, (state) => {
      state.phoneVerification = {
        ...state.phoneVerification,
        loading: true,
        verifyCodeSuccessful: false,
        errorMessage: null,
      };
    });
    builder.addCase(sendPhoneVerificationCode.fulfilled, (state) => {
      state.phoneVerification = {
        ...state.phoneVerification,
        loading: false,
        readyVerifyCode: false,
        verifyCodeSuccessful: true,
      };
    });
    builder.addCase(sendPhoneVerificationCode.rejected, (state, action) => {
      state.phoneVerification = {
        ...state.phoneVerification,
        loading: false,
        errorMessage: action.payload as IErrorMessage,
      };
    });
  },
});

export const StudyAction = studySlice.actions;
export default studySlice.reducer;
